import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  FloatButton,
  Input,
  Popconfirm,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip
} from 'antd';
import {
  ArrowDownOutlined,
  CloseOutlined,
  DownOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  RobotOutlined,
  SendOutlined,
  SyncOutlined
} from "@ant-design/icons";
import Logo from "../../common/logo";
import { PUBLIC_ASSETS_URL } from "../../../config";
import { generateRandomString } from "../../../helpers/converters";
import { load, save } from "../../../helpers/local-storage";
import { storeMessages } from "../../../services/api-users";
import { getFullLanguage } from "../../../services/api-patents";
import { getDocumentBasedResponse, getKnowledgeEnhancedResponse } from "../../../services/api-patty";
import SoundSVG from '../../../assets/icon/sound_icon.svg';
import MuteSoundSVG from '../../../assets/icon/mute_sound_icon.svg';
import BrainSVG from '../../../assets/icon/brain_icon.svg';
import { withUser } from "@unifiedpatents/sso";
import Icon from '@ant-design/icons';

const MESSAGE_COUNT_LIMIT = 10;
const MAX_MESSAGE_LENGTH = 300;
const MessageType = Object.freeze({
  PATTY_USER: 'user',
  PATTY_BOT: 'bot',
  PATTY_ERROR: 'error'
});
const PRE_DEFINED_QUESTIONS = [
  "Help me to summarize this patent.",
  "What are some good keywords I can use to find prior art?",
  "What is the novelty of this patent?",
];
const INITIAL_MESSAGE = {
  message_content: "Hello! I’m Patty, your assistant for patent-related questions. How may I assist you today?\n\n" +
        "You can ask me any questions related to the content of the patent you're currently viewing. Whether it's about the " + 
        "inventor, claims, abstract, or any other specific section. " +
        "Please note that I can only provide information and answer questions based on the content available on this page.\n\n" +
        "Just type your question below and I'll do my best to help.\n",
  message_type: MessageType.PATTY_BOT,
  date: new Date().toISOString()
};
const PATTY_POP_SOUND = PUBLIC_ASSETS_URL + "/patty/patty_pop_sound.wav";
const KNOWLEDGE_ENHANCED_TOGGLE_MESSAGE = "Enriched Mode: Offers answers using both document content and the model's external knowledge for more comprehensive insights.";
const DOCUMENT_ONLY_TOGGLE_MESSAGE = "Document-Only Mode: Provides answers strictly based on the document content. Cannot offer answers if information is not found in the patent's content.";
// Fields from the assets that will be included in the question's reference documents
const DOCUMENT_REFERENCE_FIELDS = [
  "application_date",
  "assignee_current",
  "assignee_original",
  "assignee_parent",
  "country",
  "cpc_class",
  "examiner",
  "expiration_date",
  "grant_date",
  "inventors",
  "is_challenged",
  "is_litigated",
  "kind_code",
  "last_challenged_at",
  "last_litigated_at",
  "num_challenged",
  "num_litigated",
  "priority_date",
  "publication_status",
  "publication_type"
];
const styles = {
  chatContainer: {
    position: 'fixed',
    bottom: '12%',
    right: '3%',
  },
  card: {
    width: '35vw',
    maxHeight: '70vh',
    overflowY: 'auto',
    border: '6px solid #E5E4E2',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
  },
  messagesBox: {
    maxHeight: '45vh',
    overflowY: 'auto',
    padding: '0.3em 0.6em',
  },
  botMessage: {
    display: 'inline-block',
    padding: '0.3em 0.6em',
    borderRadius: '0.6em',
    background: '#e6e6e6',
    color: 'black',
    maxWidth: '70%',
    wordBreak: 'break-word',
    whiteSpace: 'pre-line',
  },
  userMessage: {
    display: 'inline-block',
    padding: '0.3em 0.6em',
    borderRadius: '0.6em',
    background: '#4A90E2',
    color: 'white',
    maxWidth: '70%',
    wordBreak: 'break-word',
    whiteSpace: 'pre-line',
    textAlign: 'left',
  },
  errorMessage: {
    display: 'inline-block',
    padding: '0.3em 0.6em',
    borderRadius: '0.6em',
    background: '#F6d5d4',
    color: '#505152',
    maxWidth: '70%',
    wordBreak: 'break-word',
    whiteSpace: 'pre-line',
    border: '2px solid #f1807e',
  },
  predefinedQuestion: {
    marginTop: '1em',
    background: '#B6D0E2',
    borderRadius: '0.5em',
    padding: '0.3em 0.6em',
    cursor: 'pointer',
  },
};

class PatentChat extends Component {
  static propTypes = {
    assets: PropTypes.shape({
      abstract: PropTypes.string,
      publication_number: PropTypes.string
    }),
  };

  state = {
    concatenatedPatentInfo: '',
    claims: [],
    claimsLoading: true,
    description: [],
    descriptionLoading: true,
    soundOn: true,
    messages: [],
    inputValue: '',
    responseLoading: false,
    minimized: false,
    showScrollToEndButton: false,
    messageCount: 0,
    currentConversationId: generateRandomString(),
    isKnowledgeEnhancedEnabled: true,
    error: false
  };

  componentDidMount = async() => {
    const localStorage = load("minimizedPatentChat");
    if (localStorage) this.setState({ minimized: localStorage });
    this._isMounted = true;
    this._getConcatenatedPatentInfo();
    if (!this.state.error) {
      this.setState({
        messages: [INITIAL_MESSAGE],
      });
      if (!this.state.minimized) this._playSound();
    }
    if (this.messagesEnd) {
      this.messagesEnd.addEventListener('scroll', this._handleScroll);
      this.messagesEnd.addEventListener('wheel', this._handleScroll);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.chatContainer) return;
    const isVisible = window.getComputedStyle(this.chatContainer).display !== 'none'; // not visible for mobile users
    if (isVisible && this.state.messages.length >= 1 && prevState.messages.length !== this.state.messages.length) {
      // Play sound when a new response from PATTY_BOT is added
      const lastMessage = this.state.messages[this.state.messages.length - 1];
      if (this.state.messages.length > 1 && lastMessage.message_type === MessageType.PATTY_BOT) {
        this._playSound();
        // TODO: Currently, _storeConversationHistory is not triggered in componentWillUnmount when the user 
        // refreshes the page. This is causing us to store the entire conversation every time a new response 
        // is received, leading to the storage of a substantial amount of duplicate messages. This needs to be
        // optimized and handled more efficiently in the future to avoid redundancy and improve performance.
        this._storeConversationHistory();
      }
      if (this.messagesEnd) {
        this._scrollToBottom();
      }
    }
  }

  componentWillUnmount() {
    this._storeConversationHistory();
    if (this.messagesEnd) {
      this.messagesEnd.removeEventListener('scroll', this._handleScroll);
      this.messagesEnd.removeEventListener('wheel', this._handleScroll);
    }
  }
  render() {
    const title = (
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
        <div>Ask Patty</div>
        <Tag color="#2db7f5" style={{ marginLeft: '2%' }} >{"Beta"}</Tag>
        <Button 
            icon={<Icon component={this.state.soundOn ? SoundSVG : MuteSoundSVG}/>} 
            ghost={true} 
            onClick={this._toggleSound}
          />
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Tooltip title={this.state.isKnowledgeEnhancedEnabled? KNOWLEDGE_ENHANCED_TOGGLE_MESSAGE : DOCUMENT_ONLY_TOGGLE_MESSAGE}>
            <Switch 
              checkedChildren={<BrainSVG />} 
              unCheckedChildren={<CloseOutlined />} 
              checked={this.state.isKnowledgeEnhancedEnabled}
              onChange={this._toggleKnowledgeEnhancedEnabled}
            />
          </Tooltip>
          <Popconfirm
            title="Restart Patty?"
            description={
              <>
                Restarting Patty will clear all the history<br />
                and start a new conversation
              </>
            }
            onConfirm={this._handleRestart}
            icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
            placement="topRight"
          >
            <Button
              ghost={true}
              icon={<ReloadOutlined style={{ color: 'black' }} />}
              disabled={this.state.messageCount >= MESSAGE_COUNT_LIMIT}
            />
          </Popconfirm>
        </div>
      </div>
    );
    
    return (
      <div 
        style={styles.chatContainer} 
        className={this.props.className} 
        ref={(el) => { this.chatContainer = el; }}
      >
        {!this.state.minimized && (
          <Card title={title} style={styles.card}>
            <div>
              <div style={styles.messagesBox} ref={(el) => { this.messagesEnd = el; }}>
                {this.state.messages.map((message, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: '3%',
                      textAlign:
                        message.message_type === MessageType.PATTY_BOT || message.message_type === MessageType.PATTY_ERROR
                          ? 'left'
                          : 'right',
                    }}
                  >
                    {(
                      message.message_type === MessageType.PATTY_BOT ||
                      message.message_type === MessageType.PATTY_ERROR
                    ) && (
                      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
                        <a href="https://www.unifiedpatents.com/" target="_blank" rel="noopener noreferrer">
                          <Logo style={{ marginRight: '0.5em' }}/>
                        </a>
                        <div
                          style={
                            message.message_type === MessageType.PATTY_BOT
                              ? styles.botMessage
                              : styles.errorMessage
                          }
                        >
                          {message.message_content}
                          {message.message_content.includes("Just type your question") &&  // PRE_DEFINED_QUESTIONS
                            PRE_DEFINED_QUESTIONS.map((question, qIndex) => (
                              <div
                                key={qIndex}
                                onClick={() => this._handlePredefinedQuestionClick(question)}
                                style={styles.predefinedQuestion}
                              >
                                {question}
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    )}
                    {message.message_type === MessageType.PATTY_USER && 
                      <div style={styles.userMessage}>
                        {message.message_content}
                      </div>
                    }
                  </div>
                  ))
                }
                {this.state.responseLoading && <Spin />}
              </div>
              {this.state.showScrollToEndButton && (
                <div style={{ textAlign: 'center', marginTop: "2%" }}>
                  <Button 
                    type="primary" 
                    icon={<ArrowDownOutlined />} 
                    onClick={this._scrollToBottom}
                  >
                    Scroll to end of chat
                  </Button>
                </div>
              )}
              {
                !this.state.responseLoading &&
                this.state.messages.length > 1 &&
                !this.state.showScrollToEndButton && 
                this.state.messageCount < MESSAGE_COUNT_LIMIT && (
                  <div style={{ textAlign: 'right', marginTop: "1%" }}>
                    <Button 
                      icon={<SyncOutlined />} 
                      onClick={this._handleRegenerate}
                      style={{backgroundColor: '#ADD8E6'}}
                    >
                      Regenerate
                    </Button>
                  </div>
                )
              }
              <div style={{marginTop: '2%', display: 'flex' }}>
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    value={this.state.inputValue}
                    placeholder={this.state.messageCount >= MESSAGE_COUNT_LIMIT ? "Limit reached. Please start a new conversation." : "Type your question here."}
                    onChange={this._handleInputChange}
                    onKeyPress={this._handleUserMessage}
                    disabled={this.state.messageCount >= MESSAGE_COUNT_LIMIT}
                  />
                  <Button 
                    type="primary" 
                    icon={<SendOutlined />} 
                    onClick={() => this._sendMessage(
                      true,
                      false,
                      this.state.inputValue, 
                      this.state.currentConversationId
                    )}
                    disabled={
                      this.state.responseLoading ||
                      this.state.messageCount >= MESSAGE_COUNT_LIMIT
                    }
                  />
                </Space.Compact>
              </div>
              <div style={{ 
                textAlign: 'center',
                fontSize: '1em', 
                color: this.state.messageCount >= MESSAGE_COUNT_LIMIT ? '#ff0f0f' : '#D3D3D3' 
              }}>
                Message count: {this.state.messageCount}/{MESSAGE_COUNT_LIMIT}
              </div>
            </div>
          </Card>)}
          <FloatButton
            type="primary"
            style={{ width: '4vw', height: '6vh' }}
            icon={this.state.minimized? <RobotOutlined /> : <DownOutlined />}
            onClick={this._handleMinimize}
          />
      </div>
    );
  }

  // Due to Chrome's autoplay policy, play() may fail if the user hasn't interacted with the document, 
  // as interaction is required to play sound. Catch any failures to prevent errors.
  _playSound = () => {
    if (this.state.soundOn) {
      const audio = new Audio(PATTY_POP_SOUND);
      audio.play().catch(() => {
        return;
      });
    }
  };

  _getConcatenatedPatentInfo = () => {
    let concatenatedData = '';
    DOCUMENT_REFERENCE_FIELDS.forEach(field => {
      if (Object.prototype.hasOwnProperty.call(this.props.assets, field)) {
        let value = this.props.assets[field];
        concatenatedData += (field + ': ')
        if (Array.isArray(value)) {
          concatenatedData += value.flat(Infinity).map(item => {
            if (typeof item === 'object' && item !== null) {
              return JSON.stringify(item);
            }
            return item;
          }).join(', ');
        } else if (typeof value === 'object' && value !== null) {
          concatenatedData += JSON.stringify(value);
        } else {
          concatenatedData += value;
        }
        concatenatedData += ', ';
      }
    });
    this.setState({
      concatenatedPatentInfo: concatenatedData.trim()
    });
  };

  _getFullLanguage = async () => {
    try {
      const json = await getFullLanguage(this.props.assets.publication_number);
      if (this._isMounted) {
        this.setState({
          claims: json.claims,
          description: json.description,
          claimsLoading: false,
          descriptionLoading: false,
          error: false
        });
      }
      return;
    } catch (error) {
      if (this._isMounted) {
        this.setState({
          error: true
        });
      }
    }
  }

  _scrollToBottom = () => {
    this.messagesEnd.scrollTop = this.messagesEnd.scrollHeight;
  }

  _isScrolledToEnd = () => {
    const threshold = 5;
    return this.messagesEnd.scrollHeight - this.messagesEnd.scrollTop <= this.messagesEnd.clientHeight + threshold;
  };

  _handleScroll = (e) => {
    if (this._isScrolledToEnd()) {
      this.setState({ showScrollToEndButton: false });
    } else {
      this.setState({ showScrollToEndButton: true });
    }
    // Prevent parent from scrolling
    const target = e.currentTarget;
    if (e.deltaY < 0 && target.scrollTop <= 0) {
      // Scrolling up, but this div is already at the top
      e.preventDefault();
    }
    if (e.deltaY > 0 && target.scrollHeight - target.clientHeight <= target.scrollTop) {
      // Scrolling down, but this div is already at the bottom
      e.preventDefault();
    }
  }; 

  _toggleKnowledgeEnhancedEnabled = () => {
    this.setState(prevState => ({
      isKnowledgeEnhancedEnabled: !prevState.isKnowledgeEnhancedEnabled
    }));
  };

  _toggleSound = () => {
    this.setState(prevState => ({
      soundOn: !prevState.soundOn
    }));
  };

  _handlePredefinedQuestionClick = (question) => {
    if (this.state.responseLoading || this.state.messageCount >= MESSAGE_COUNT_LIMIT) {
      return;
    }
    this._sendMessage(false, false, question, this.state.currentConversationId);
  };

  _handleMinimize = () => {
    this.setState(prevState => ({
      minimized: !prevState.minimized
    }), () => {
      save("minimizedPatentChat", this.state.minimized);
      if (!this.state.minimized) {
        if (this.messagesEnd) {
          this.messagesEnd.addEventListener('scroll', this._handleScroll);
          this.messagesEnd.addEventListener('wheel', this._handleScroll);
          this._scrollToBottom();
        }
      }
    });
  };

  _handleRegenerate = () => {
    if (!this._isMounted) return;
    if (this.state.messages.length === 0) return null;
    this.setState((prevState) => ({
      messages: prevState.messages.slice(0, -1), // Remove the last message to regenerate it
      error: false,
    }), () => {
      const lastUserMessage = [...this.state.messages].reverse().find(
        msg => msg.message_type === MessageType.PATTY_USER
      );
      if (lastUserMessage) {
        this._sendMessage(false, true, lastUserMessage.message_content, this.state.currentConversationId);
      }
    });
  };
  
  _handleRestart = () => {
    const newConversationId = generateRandomString();
    this.setState({
      messages: [],
      inputValue: '',
      showScrollToEndButton: false,
      currentConversationId: newConversationId,
      responseLoading: true,
      messageCount: 0,
      error: false
    });
    setTimeout(() => {
      if (!this.state.error) {
        this.setState({
          messages: [INITIAL_MESSAGE],
          responseLoading: false
        });
      }
    }, 1000);
  };
  
  _handleInputChange = (e) => {
    this.setState({ inputValue: e.target.value });
  };

  _handleUserMessage = (e) => {
    if (this.state.responseLoading) {
      return;
    }
    if (e.key === 'Enter' && this.state.inputValue.trim() !== "") {
      this._sendMessage(true, false, this.state.inputValue, this.state.currentConversationId);
    }
  };

  _storeConversationHistory = async () => {
    // Only contains the initial message
    if (this.state.messages.length <= 1) return;
    try {
      const response = await storeMessages({
        messages: this.state.messages.slice(1), // ignore the first initial message
        patent_number: this.props.assets.publication_number,
        conversation_id: this.state.currentConversationId
      });
      return response;
    } catch (error) {
      // Error storing conversation history
    }
  };

  _sendMessage = async (clearInputValue, isRegenerate, messageText, conversationId) => {
    if (
      !messageText.trim() ||
      this.state.responseLoading
    ) {
      return;
    }
    if (this.state.description.length === 0) await this._getFullLanguage();
    if (clearInputValue) {
      this.setState({ inputValue: '' });
    }
    if (messageText.split(/\s+/).length > MAX_MESSAGE_LENGTH) {
      this._appendMessage(messageText, MessageType.PATTY_USER)
      this._handleError('The message you try to submit is too long, please submit something shorter.');
      return;
    }
    if (!isRegenerate) {
      this._appendMessage(messageText, MessageType.PATTY_USER)
    }
    if (this.state.claims.length === 0) {
      this._handleError('Unable to retrieve full language for this patent. Please try again later.');
      return;
    }
    this.setState({responseLoading: true });
    try {
      const text = await this._getResponse(messageText);
      if (conversationId === this.state.currentConversationId) {
        this._appendMessage(text, MessageType.PATTY_BOT)
        if (this._isMounted) {
          this.setState((prevState) => ({
            messageCount: prevState.messageCount + 1,
            responseLoading: false
          }));
        }
      }
    } catch (error) {
      if (conversationId === this.state.currentConversationId) {
        this._handleError();
      }
    }
  }

  _extractClaimTexts = (claims) => {
    return claims.flatMap(claim => {
      if (claim.children && claim.children.length) {
        return [claim.text, ...this._extractClaimTexts(claim.children)];
      }
      return claim.text;
    });
  }  

  _getResponse = async (question) => {
    const payload = {
      question: question,
      docs: [
        this.state.concatenatedPatentInfo,
        `abstract:${this.props.assets.abstract}`, // Add "abstract" keyword for similarity search.
        this.state.description.flat(Infinity).join(' '), // The description will be split into multiple documents in the backend, so there's no need to add a keyword.
        `claims: ${this._extractClaimTexts(this.state.claims).join(' ')}` // Add "claims" keyword for similarity search.
      ],
      chat_history: this.state.messages
    };
    if (this.state.isKnowledgeEnhancedEnabled) {
      return await getKnowledgeEnhancedResponse(payload);
    } else {
      return await getDocumentBasedResponse(payload);
    }
  };  

  _appendMessage = (content, type) => {
    const message = {
      message_content: content,
      message_type: type,
      date: new Date().toISOString()
    };
    if (this._isMounted) {
      this.setState((prevState) => ({
        messages: [...prevState.messages, message],
      }));
    }
  }

  _handleError = (errorMessage = 'Oops. Something went wrong. Please try again later.') => {
    this._appendMessage(errorMessage, MessageType.PATTY_ERROR);
    if (this._isMounted) {
      this.setState(() => ({
        responseLoading: false,
        error: true
      }));
    }
  };
}

export default withUser(PatentChat);